<template>
  <p-data-table
    v-bind="$attrs"
    v-model:filters="filters"
    :value="tableData"
    :global-filter-fields="Object.keys(filters)"
    filter-display="menu"
    :pt="{
      column: {
        filter: {
          style: 'margin-inline-start: 0;'
        }
      }
    }"
  >
    <p-column
      v-for="field in normalizedFields"
      :key="field.key || field.slot"
      :field="field.key"
      :header="field.label"
      :sortable="field.sortable"
      :data-type="field.dataType"
      :sort-field="field.sortByFormatted ? (data) => field.formatter(data[field.key], field, data) : undefined"
      :style="getColumnStyle(field)"
      :class="field.class"
      :header-class="field.headerClass"
      :body-class="field.tdClass"
      :show-filter-operator="filters[field.key]?.constraints?.length > 1"
    >
      <!-- Column Header Slot -->
      <template
        v-if="hasHeaderSlot(field)"
        #header
      >
        <slot
          :name="getHeaderSlotName(field)"
          :label="field.label"
          :field="field"
          :column="field"
        >
          {{ field.label }}
        </slot>
      </template>

      <!-- Column Body/Cell Slot -->
      <template
        v-if="hasCellSlot(field) || field.formatter"
        #body="slotProps"
      >
        <slot
          :name="getCellSlotName(field)"
          :value="getCellValue(slotProps.data, field)"
          :item="slotProps.data"
          :field="field"
          :index="slotProps.index"
        >
          {{ formatCellValue(slotProps.data, field) }}
        </slot>
      </template>

      <!--       filters -->
      <template
        v-if="field.filter"
        #filter="{ filterModel }"
      >
        <template
          v-for="filterType in field.filterOptions"
          :key="filterType"
        >
          <p-input-text
            v-if="filterType === 'string'"
            v-model="filterModel.value"
          />
          <t-radio-group
            v-else-if="Array.isArray(filterType)"
            v-model="filterModel.value"
            stacked
            :options="filterType"
          />
          <p-input-number
            v-else-if="filterType === 'numeric'"
            v-model="filterModel.value"
          />
          <single-date-picker
            v-else-if="filterType === 'date'"
            :model-value="filterModel.value"
            embed
            min-date="2021-01-01"
            @update:modelValue="filterModel.value = new Date($event)"
          />
        </template>
      </template>
      <template
        v-if="field.filter"
        #filtericon
      >
        <fluency-icon
          type="filter"
          :class="{ 'has-filters': filters[field.key].constraints.some((constraint) => constraint.value !== null) }"
          style="top: 0"
        ></fluency-icon>
      </template>
    </p-column>

    <!-- Pass through other DataTable slots -->
    <template
      v-if="$slots.empty"
      #empty
    >
      <slot name="empty" />
    </template>

    <template
      v-if="$slots.loading"
      #loading
    >
      <slot name="loading" />
    </template>

    <template
      v-if="$slots.expansion"
      #expansion="slotProps"
    >
      <slot
        name="expansion"
        :data="slotProps.data"
        :index="slotProps.index"
      />
    </template>

    <!-- Support Bootstrap-Vue row-details slot as alias for expansion -->
    <template
      v-if="$slots['row-details'] && !$slots.expansion"
      #expansion="slotProps"
    >
      <slot
        name="row-details"
        :item="slotProps.data"
        :index="slotProps.index"
        :toggle-details="() => toggleRowExpansion(slotProps.data)"
      />
    </template>
  </p-data-table>
</template>

<script setup>
import { computed, useSlots } from 'vue'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import singleDatePicker from 'backpack/src/components/common/dateSelector/components/singleDatePicker.vue'
const { $filters } = useNuxtApp()
const props = defineProps({
  /**
   * Array of field definitions compatible with Bootstrap-Vue table format
   * Each field object can contain:
   * - key: string - Property name in the data
   * - label: string - Column header text
   * - sortable: boolean - Enable sorting
   * - formatter: function - Format cell value for display
   * - sortByFormatted: function - Custom sort logic
   * - filterByFormatted: function - Custom filter logic
   * - tdClass: string - CSS class for cells
   * - thClass: string - CSS class for headers
   * - class: string - CSS class for column
   * - headerClass: string - CSS class for header
   * - slot: string - Custom slot name
   * - defaultWidth: number - Default column width
   * - sticky: boolean - Make column sticky
   * - resizable: boolean - Allow column resizing
   */
  fields: {
    type: Array,
    required: true,
    default: () => []
  },

  /**
   * Table data array (PrimeVue uses 'value' prop)
   */
  value: {
    type: Array,
    default: () => []
  },

  /**
   * Alternative prop name for table data (Bootstrap-Vue compatibility)
   */
  items: {
    type: Array,
    default: () => []
  }
})
const emit = defineEmits(['toggle-details'])
const slots = useSlots()
const filters = ref({})

// Normalize fields array to ensure all fields have required properties
const normalizedFields = computed(() => {
  return props.fields.map((field) => {
    // Support both string and object field definitions
    if (typeof field === 'string') {
      return {
        key: field,
        label: field,
        sortable: false
      }
    }

    return {
      key: field.key,
      label: field.label || $filters.convertFromCamelCase(field.key),
      sortable: field.sortable || field.filter || false,
      formatter: field.formatter,
      sortByFormatted: field.sortByFormatted,
      filterByFormatted: field.filterByFormatted,
      tdClass: field.tdClass || field.class,
      headerClass: field.thClass || field.headerClass,
      class: field.class,
      slot: field.slot,
      defaultWidth: field.defaultWidth,
      sticky: field.sticky,
      resizable: field.resizable,
      filter: field.filter,
      filterOptions: field.filterOptions,
      ...field // Pass through any other properties
    }
  })
})

// Get actual table data (support both 'value' and 'items' props)
const tableData = computed(() => {
  return props.value?.length ? props.value : props.items
})

// Check if a header slot exists for a field
const hasHeaderSlot = (field) => {
  const slotName = getHeaderSlotName(field)
  return !!slots[slotName]
}

// Check if a cell slot exists for a field
const hasCellSlot = (field) => {
  const slotName = getCellSlotName(field)
  return !!slots[slotName]
}

// Get header slot name in Bootstrap-Vue format: head(key) or head()
const getHeaderSlotName = (field) => {
  if (field.slot) {
    return `head(${field.slot})`
  }
  return field.key ? `head(${field.key})` : 'head()'
}

// Get cell slot name in Bootstrap-Vue format: cell(key)
const getCellSlotName = (field) => {
  if (field.slot) {
    return `cell(${field.slot})`
  }
  return `cell(${field.key})`
}

// Get value from data object for a field
const getCellValue = (data, field) => {
  if (!data || !field.key) return null

  // Support nested properties with dot notation
  const keys = field.key.split('.')
  let value = data

  for (const key of keys) {
    if (value && typeof value === 'object') {
      value = value[key]
    } else {
      return null
    }
  }

  return value
}

// Format cell value using field formatter if provided
const formatCellValue = (data, field) => {
  const value = getCellValue(data, field)

  if (field.formatter && typeof field.formatter === 'function') {
    return field.formatter(data[field.key], field, data)
  }

  return value
}

// Get column style based on field configuration
const getColumnStyle = (field) => {
  const styles = {}

  if (field.defaultWidth) {
    styles.width = `${field.defaultWidth}px`
  }

  if (field.sticky) {
    styles.position = 'sticky'
    styles.left = '0'
    styles.zIndex = '1'
  }

  return styles
}

// Toggle row expansion (for row-details compatibility)
const toggleRowExpansion = (rowData) => {
  // This would need to be implemented based on PrimeVue's expandedRows model
  // Emitting event for parent to handle
  emit('toggle-details', rowData)
}

watch(
  () => props.fields,
  (value) => {
    const f = {}
    for (const field of value) {
      if (field.filter) {
        f[field.key] = {
          operator: FilterOperator.AND,
          showOperator: false,
          constraints: field.filterOptions.map((option) => ({
            value: null,
            matchMode: option === 'numeric' ? FilterMatchMode.EQUALS : option === 'date' ? FilterMatchMode.DATE_IS : FilterMatchMode.CONTAINS
          }))
        }
      }
    }
    filters.value = f
  },
  { immediate: true }
)
// Expose methods for parent components if needed
defineExpose({
  tableData,
  normalizedFields
})
</script>

<style scoped lang="scss">
.has-filters {
  color: $bionic-primary-300;
  :deep(svg) {
    fill: $bionic-primary-300;
  }
}
:deep(.table-hover-icon-btn) {
  border-radius: 50%;
  height: var(--p-button-icon-only-width);
  width: var(--p-button-icon-only-width);
  visibility: hidden;
}
:deep(.p-datatable-tbody > tr:hover .table-hover-icon-btn) {
  visibility: visible;
}
:deep(.table-hover-icon-btn:focus) {
  visibility: visible;
}
</style>
